import React, { useEffect, useState } from "react";
import { Input, Select } from "antd";
import {
  COUNTRY_CODES,
  formatPhoneNumber,
  getDefaultCountryCode,
  parsePhoneNumber,
} from "../utils/phoneCountryCodes";

interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Restrict the prefix selector to these ISO codes (e.g. ['ES', 'IT', 'PT']).
   * When omitted, the full COUNTRY_CODES list is shown.
   */
  countryFilter?: string[];
  /**
   * Override where the prefix dropdown mounts. Default anchors it to
   * the trigger's parent so it stays inside Modals / Drawers without
   * a custom z-index. Pass `() => document.body` for the cases where
   * the parent clips the popup (deeply nested forms inside scrollable
   * modal bodies) — keeps the dropdown floating above everything.
   */
  popupContainer?: (triggerNode: HTMLElement) => HTMLElement;
}

/**
 * Phone input with international prefix selector.
 *
 * - Trigger shows just the flag and the dial code (compact).
 * - Dropdown is fixed-width (320px) so country names always fit and every
 *   country in COUNTRY_CODES is visible without horizontal clipping.
 * - Search matches by country name, ISO code or dial code.
 * - Output: `+XX XXXXXXXXX` (always with the prefix prepended).
 */
export const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  placeholder = "Phone number",
  disabled = false,
  countryFilter,
  popupContainer,
}) => {
  const [dialCode, setDialCode] = useState<string>(getDefaultCountryCode().dialCode);
  const [phoneNumber, setPhoneNumber] = useState<string>("");

  const visibleCountries = countryFilter
    ? COUNTRY_CODES.filter((c) => countryFilter.includes(c.code))
    : COUNTRY_CODES;

  useEffect(() => {
    if (!value) return;
    const parsed = parsePhoneNumber(value);
    if (parsed) {
      setDialCode(parsed.dialCode);
      setPhoneNumber(parsed.number);
    }
  }, [value]);

  const handleDialCodeChange = (newDialCode: string) => {
    setDialCode(newDialCode);
    onChange?.(formatPhoneNumber(newDialCode, phoneNumber));
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newNumber = e.target.value.replace(/[^\d]/g, ""); // digits only
    setPhoneNumber(newNumber);
    onChange?.(formatPhoneNumber(dialCode, newNumber));
  };

  const options = visibleCountries.map((country) => ({
    value: country.dialCode,
    // Compact dropdown row: just flag + dial code (matches trigger width).
    label: (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 16 }}>{country.flag}</span>
        <span style={{ fontWeight: 500 }}>{country.dialCode}</span>
      </span>
    ),
    // Backing data for search — kept full so users can still type country names.
    searchKey: `${country.name} ${country.code} ${country.dialCode}`.toLowerCase(),
  }));

  const prefixSelector = (
    <Select
      value={dialCode}
      onChange={handleDialCodeChange}
      disabled={disabled}
      showSearch
      // Tight width so the closed trigger fits "🇪🇸 +34" without excess
      // left margin caused by AntD's default selection-item padding.
      style={{ width: 92 }}
      // Match the trigger width — the dropdown shows only flag + prefix.
      popupMatchSelectWidth={true}
      // Anchor the dropdown to the trigger's parent by default so it renders
      // inside any surrounding Modal / Drawer and doesn't get hidden by their
      // z-index. Callers can override with `popupContainer` (typically
      // `() => document.body`) when the parent clips the popup — e.g. a
      // PhoneInput placed near the bottom of a scrollable modal body.
      getPopupContainer={
        popupContainer
          ? (node) => popupContainer(node as HTMLElement)
          : (node) => (node?.parentElement as HTMLElement) ?? document.body
      }
      dropdownStyle={popupContainer ? { zIndex: 10000 } : undefined}
      // Custom search against `searchKey` we packed into each option.
      filterOption={(input, option) =>
        ((option as any)?.searchKey ?? "")
          .includes(input.toLowerCase())
      }
      // What the closed trigger shows: only flag + dial code.
      optionLabelProp="label"
      // Custom render of the selected (closed) state — compact, fits the 100px trigger.
      labelRender={(props) => {
        const country = COUNTRY_CODES.find((c) => c.dialCode === props.value);
        if (!country) return <span>{props.value as string}</span>;
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span>{country.flag}</span>
            <span>{country.dialCode}</span>
          </span>
        );
      }}
      options={options}
    />
  );

  return (
    <Input
      addonBefore={prefixSelector}
      value={phoneNumber}
      onChange={handlePhoneNumberChange}
      placeholder={placeholder}
      disabled={disabled}
      maxLength={15}
    />
  );
};

export default PhoneInput;
